import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { gbp } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Repeat, Flag, XCircle, RotateCcw, PiggyBank } from "lucide-react";

const STATUS_STYLE = {
  active: "border-[#00C896]/30 bg-[#00C896]/10 text-[#4DE3BF]",
  flagged: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  cancelled: "border-rose-500/30 bg-rose-500/10 text-rose-300",
};

export default function SubscriptionDetector({ data, onReload }) {
  const { subscriptions = [], total_annual = 0, potential_savings = 0 } = data || {};

  const setStatus = async (merchant, status) => {
    try {
      await api.post(`/subscriptions/status?merchant=${encodeURIComponent(merchant)}`, { status });
      toast.success(
        status === "cancelled" ? `Marked ${merchant} as cancelled` :
        status === "flagged" ? `Flagged ${merchant} for review` : `Restored ${merchant}`
      );
      onReload?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="mm-glow-card p-6 h-full" data-testid="subscription-detector">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#00C896]">Subscriptions radar</p>
          <h3 className="font-display text-lg font-semibold mt-1">Recurring charges</h3>
        </div>
        <span className="h-10 w-10 rounded-xl bg-[#00C896]/15 flex items-center justify-center text-[#00C896]">
          <Repeat className="h-5 w-5" />
        </span>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-white/8 bg-[#0D0D0D] p-3">
          <p className="text-xs text-slate-500">Active yearly cost</p>
          <p data-testid="subs-annual" className="font-num text-lg font-bold text-white mt-0.5">{gbp(total_annual)}</p>
        </div>
        <div className="rounded-xl border border-[#00C896]/20 bg-[#00C896]/5 p-3">
          <p className="text-xs text-slate-500 flex items-center gap-1"><PiggyBank className="h-3 w-3" /> Potential savings</p>
          <p data-testid="subs-savings" className="font-num text-lg font-bold text-[#00C896] mt-0.5">{gbp(potential_savings)}</p>
        </div>
      </div>

      <div className="mt-4 space-y-2.5 max-h-[320px] overflow-y-auto pr-1">
        {subscriptions.map((s) => (
          <div key={s.merchant} data-testid={`subscription-${s.merchant}`} className="rounded-xl border border-white/8 bg-[#0D0D0D] p-3.5">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className={`text-sm font-medium truncate ${s.status === "cancelled" ? "line-through text-slate-500" : ""}`}>{s.merchant}</p>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${STATUS_STYLE[s.status]}`}>{s.status}</span>
                </div>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  <span className="font-num">{gbp(s.amount)}</span> / {s.cadence} · {gbp(s.annual_cost)}/yr · {s.charges} charges
                </p>
              </div>
            </div>
            <div className="mt-2.5 flex items-center gap-2">
              {s.status !== "flagged" && s.status !== "cancelled" && (
                <Button data-testid={`flag-${s.merchant}`} onClick={() => setStatus(s.merchant, "flagged")} size="sm" variant="outline" className="h-7 rounded-full border-amber-500/40 bg-transparent text-amber-300 hover:bg-amber-500/10 text-xs px-3">
                  <Flag className="h-3 w-3 mr-1" /> Flag
                </Button>
              )}
              {s.status !== "cancelled" && (
                <Button data-testid={`cancel-${s.merchant}`} onClick={() => setStatus(s.merchant, "cancelled")} size="sm" variant="outline" className="h-7 rounded-full border-rose-500/40 bg-transparent text-rose-300 hover:bg-rose-500/10 text-xs px-3">
                  <XCircle className="h-3 w-3 mr-1" /> Cancel
                </Button>
              )}
              {(s.status === "cancelled" || s.status === "flagged") && (
                <Button data-testid={`restore-${s.merchant}`} onClick={() => setStatus(s.merchant, "active")} size="sm" variant="outline" className="h-7 rounded-full border-white/15 bg-transparent text-slate-300 hover:bg-white/5 text-xs px-3">
                  <RotateCcw className="h-3 w-3 mr-1" /> Restore
                </Button>
              )}
            </div>
          </div>
        ))}
        {subscriptions.length === 0 && (
          <p className="text-sm text-slate-500 py-4 text-center">No recurring charges detected yet. Connect a bank with transaction history.</p>
        )}
      </div>
    </div>
  );
}
